import React, { useState } from 'react';
import { Modal } from '@common/Modal';
import { Button } from '@common/Button';
import { Download, FileText, Map, Archive, AlertTriangle } from 'lucide-react';
import { exportData } from '@utils/export';
import { logError, logInfo } from '@utils/logger';
import { Toast } from '@common/Toast';

export const ExportModal = ({ isOpen, onClose, survey }) => {
  const [format, setFormat] = useState('geojson');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [options, setOptions] = useState({
    includePhotos: true,
    includeNotes: true,
    includeMetadata: true
  });

  const handleExport = async () => {
    if (!survey) {
      setError('No survey selected for export');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const blob = await exportData(survey, format.toLowerCase(), options);
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${survey.name.replace(/\s+/g, '_')}_${format}.${getFileExtension(format)}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setToast({
        type: 'success',
        message: `Survey exported successfully as ${format.toUpperCase()}`
      });
      
      logInfo(`Survey exported as ${format}`);
      
      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      logError('Export failed:', error);
      setError(error.message || 'Failed to export survey');
      setToast({
        type: 'error',
        message: error.message || 'Failed to export survey'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFileExtension = (format) => {
    switch (format.toLowerCase()) {
      case 'geojson': return 'geojson';
      case 'kml': return 'kml';
      case 'kmz': return 'kmz';
      case 'gpx': return 'gpx';
      case 'zip': return 'zip';
      default: return format.toLowerCase();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Survey">
      <div className="p-6 space-y-6">
        {/* Format Selection */}
        <div>
          <h3 className="text-white font-medium mb-3">Export Format</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              onClick={() => setFormat('geojson')}
              className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                format === 'geojson' 
                  ? 'bg-blue-500/20 text-blue-400 ring-2 ring-blue-500/50' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Map className="w-6 h-6" />
              <span>GeoJSON</span>
            </button>
            
            <button
              onClick={() => setFormat('kml')}
              className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                format === 'kml' 
                  ? 'bg-green-500/20 text-green-400 ring-2 ring-green-500/50' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Map className="w-6 h-6" />
              <span>KML</span>
            </button>
            
            <button
              onClick={() => setFormat('kmz')}
              className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                format === 'kmz' 
                  ? 'bg-purple-500/20 text-purple-400 ring-2 ring-purple-500/50' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Archive className="w-6 h-6" />
              <span>KMZ</span>
            </button>
            
            <button
              onClick={() => setFormat('gpx')}
              className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                format === 'gpx' 
                  ? 'bg-yellow-500/20 text-yellow-400 ring-2 ring-yellow-500/50' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Map className="w-6 h-6" />
              <span>GPX</span>
            </button>
            
            <button
              onClick={() => setFormat('zip')}
              className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                format === 'zip' 
                  ? 'bg-red-500/20 text-red-400 ring-2 ring-red-500/50' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Archive className="w-6 h-6" />
              <span>ZIP (All)</span>
            </button>
            
            <button
              onClick={() => setFormat('pdf')}
              className={`p-4 rounded-lg flex flex-col items-center gap-2 transition-colors ${
                format === 'pdf' 
                  ? 'bg-orange-500/20 text-orange-400 ring-2 ring-orange-500/50' 
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <FileText className="w-6 h-6" />
              <span>PDF Report</span>
            </button>
          </div>
        </div>

        {/* Export Options */}
        <div>
          <h3 className="text-white font-medium mb-3">Export Options</h3>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={options.includePhotos}
                onChange={(e) => setOptions(prev => ({ ...prev, includePhotos: e.target.checked }))}
                className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
              />
              <span className="text-gray-300">Include photos</span>
            </label>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={options.includeNotes}
                onChange={(e) => setOptions(prev => ({ ...prev, includeNotes: e.target.checked }))}
                className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
              />
              <span className="text-gray-300">Include notes</span>
            </label>
            
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={options.includeMetadata}
                onChange={(e) => setOptions(prev => ({ ...prev, includeMetadata: e.target.checked }))}
                className="rounded border-gray-600 text-blue-500 focus:ring-blue-500 bg-gray-700"
              />
              <span className="text-gray-300">Include metadata</span>
            </label>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="default"
            className="bg-gray-700 hover:bg-gray-600 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={handleExport}
            disabled={!survey || loading}
            loading={loading}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {loading ? 'Exporting...' : 'Export'}
          </Button>
        </div>
      </div>

      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Modal>
  );
};